import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { HealthMonitorService } from './services/health-monitor.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'DQP App';
  
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private healthMonitor = inject(HealthMonitorService); // Initialize health monitoring
  
  constructor() {
    this.registerCustomIcons();
  }
  
  private registerCustomIcons(): void {
    const iconPath = 'assets/icons/';
    
    // Register all custom SVG icons
    const icons = [
      { name: 'ai-brain-idea', file: 'Ai Brain Idea.svg' },
      { name: 'ai-scan', file: 'ai scan_.svg' },
      { name: 'ai-tools-alt', file: 'ai tools alt_.svg' },
      { name: 'arrow-left', file: 'Arrow - Left.svg' },
      { name: 'arrow-up-square', file: 'Arrow - Up Square.svg' },
      { name: 'chart', file: 'Chart.svg' },
      { name: 'check-circle', file: 'Check circle.svg' },
      { name: 'checkmark', file: 'Checkmark.svg' },
      { name: 'cloud-refresh', file: 'Cloud refresh.svg' },
      { name: 'data-set-1', file: 'Data Set (1).svg' },
      { name: 'data-set', file: 'Data Set.svg' },
      { name: 'database', file: 'Database 2.svg' },
      { name: 'delete', file: 'Delete.svg' },
      { name: 'diamonds', file: 'Diamonds symbols.svg' },
      { name: 'docs', file: 'Docs.svg' },
      { name: 'gem-ai', file: 'Gem ai_.svg' },
      { name: 'gem', file: 'Gem.svg' },
      { name: 'info-circle-1', file: 'info circle (1).svg' },
      { name: 'info-circle', file: 'Info Circle.svg' },
      // Newly added language icons
      { name: 'curl', file: 'curl-symbol.svg' },
      // csharp filename registered under simple key
      { name: 'csharp', file: 'csharp.svg' },
      { name: 'python', file: 'Python.svg' },
      // VB.NET icon registered for reuse as 'vbnet'
      { name: 'vbnet', file: 'VB.NET_Logo.svg' },
      { name: 'json', file: 'json.svg' },
      { name: 'microsoft-excel', file: 'Microsoft exel.svg' },
      { name: 'filter', file: 'Filter 2.svg' },
      { name: 'filter-white', file: 'Filter 2-white.svg' },
      { name: 'number-1', file: 'Number 1 Square_.svg' },
      { name: 'number-2', file: 'Number 2 Square_.svg' },
      { name: 'number-3', file: 'Number 3 Square_.svg' },
      { name: 'number-4', file: 'Number 4 Square_.svg' },
      { name: 'number-5', file: 'Number 5 Square_.svg' },
      { name: 'plus-2', file: 'Plus 2.svg' },
      { name: 'plus', file: 'Plus.svg' },
      { name: 'question-circle', file: 'question mark circle.svg' },
      { name: 'refresh', file: 'Refresh load right.svg' },
      { name: 'resize-collapse', file: 'Resize 3 arrow collapse.svg' },
      { name: 'restart', file: 'Restart.svg' },
      { name: 'search', file: 'Search.svg' },
      { name: 'setting', file: 'Setting.svg' },
      { name: 'success-documents', file: 'Success Documents.svg' },
      { name: 'tick-square', file: 'Tick Square.svg' },
      { name: 'user', file: 'user.svg' },
      // Blue variants
      { name: 'chart-blue', file: 'Chart-blue.svg' },
      { name: 'ai-scan-blue', file: 'ai scan_-blue.svg' },
      { name: 'ai-scan-white', file: 'ai scan_-white.svg' },
      { name: 'ai-brain-idea-blue', file: 'Ai Brain Idea-blue.svg' },
      { name: 'success-documents-blue', file: 'Success Documents Blue.svg' },
      { name: 'success-documents-dark-grey', file: 'Success Documents-dark-grey.svg' },
      { name: 'radar-ai-blue', file: 'radar ai_-blue.svg' },
      { name: 'radar-ai-dark-grey', file: 'radar ai_-dark-grey.svg' },
      { name: 'plus-blue', file: 'Plus-blue.svg' },
      { name: 'add-blue', file: 'Plus-blue.svg' },
      // Grey variants
      { name: 'ai-scan-grey', file: 'ai scan_-grey.svg' },
      { name: 'ai-scan-dark-grey', file: 'ai scan_-dark-grey.svg' },
      { name: 'radar-ai-grey', file: 'radar ai_-grey.svg' },
      { name: 'number-1-grey', file: 'Number 1 Square_-grey.svg' },
      { name: 'number-2-grey', file: 'Number 2 Square_-grey.svg' },
      { name: 'number-3-grey', file: 'Number 3 Square_-grey.svg' },
      { name: 'number-4-grey', file: 'Number 4 Square_-grey.svg' },
      { name: 'chart-grey', file: 'Chart-grey.svg' },
      { name: 'chart-dark-grey', file: 'Chart-dark-grey.svg' },
      { name: 'arrow-right-square-grey', file: 'Arrow - Right Square-grey.svg' },
      { name: 'arrow-right-square-blue', file: 'Arrow - Right Square-blue.svg' },
      { name: 'docs-grey', file: 'Docs-grey.svg' },
      { name: 'docs-dark-grey', file: 'Docs-dark-grey.svg' },
      { name: 'docs-blue', file: 'Docs-blue.svg' },
      { name: 'data-set-grey', file: 'Data Set-grey.svg' },
      { name: 'data-set-dark-grey', file: 'Data Set-dark-grey.svg' },
      { name: 'data-set-blue', file: 'Data Set-blue.svg' },
      { name: 'plus-grey', file: 'Plus-grey.svg' },
      { name: 'plus-dark-grey', file: 'Plus-dark-grey.svg' },
      { name: 'add-dark-grey', file: 'Plus-dark-grey.svg' },
      { name: 'flag-blue', file: 'Iconly-Flag-blue.svg' },
      { name: 'setting-blue', file: 'Iconly-Setting-blue.svg' },
      { name: 'play-blue', file: 'Iconly-Play-blue.svg' },
      // Home icon variants
      { name: 'home', file: 'home.svg' },
      { name: 'home-dark-grey', file: 'home-dark-grey.svg' },
      { name: 'home-blue', file: 'home-blue.svg' },
      // Colored gem variants
      { name: 'gem-green', file: 'Gem-green.svg' },
      { name: 'gem-ai-purple', file: 'Gem ai_-purple.svg' },
      // White variants for header
      { name: 'search-white', file: 'Search-white.svg' },
      { name: 'question-mark-white', file: 'question mark circle-white.svg' },
      { name: 'question-mark-circle', file: 'question mark circle.svg' },
      { name: 'question-mark-circle-grey', file: 'question mark circle-grey.svg' },
      { name: 'info-circle', file: 'info-circle.svg' },
      { name: 'info-circle-grey', file: 'info-circle-grey.svg' },
      { name: 'setting-white', file: 'Setting-white.svg' },
      { name: 'user-white', file: 'user-white.svg' },
      // Iconly icon replacements for Material Icons
      { name: 'expand_more', file: 'Iconly-Caret-Down-MD-1764597306995.svg' },
      { name: 'chevron_right', file: 'Iconly-Caret-Right-SM-1764597306998.svg' },
      { name: 'keyboard_arrow_up', file: 'Iconly-Caret-Up-MD-1764597307000.svg' },
      { name: 'expand_less', file: 'Iconly-Caret-Up-MD-1764597307000.svg' },
      { name: 'check_circle', file: 'Iconly-Check-circle-green.svg' },
      { name: 'check_circle_green', file: 'Iconly-Check-circle-green.svg' },
      { name: 'check_circle_blue', file: 'Iconly-Check-circle-blue.svg' },
      { name: 'check_circle_black', file: 'Iconly-Check-circle-1764597315400.svg' },
      { name: 'arrow_back', file: 'Iconly-Chevron-Left-1764597307009.svg' },
      { name: 'arrow_forward', file: 'Iconly-Chevron-Right-1764597307650.svg' },
      { name: 'schedule', file: 'Iconly-Clock-dashed-1764597319335.svg' },
      { name: 'close', file: 'Iconly-Close-remove-1764597270080.svg' },
      { name: 'close-grey', file: 'Iconly-Close-remove-1764597270080-grey.svg' },
      { name: 'cancel', file: 'Iconly-Close-Square-1764597291507.svg' },
      { name: 'delete', file: 'Iconly-Delete-1764597315594.svg' },
      { name: 'description', file: 'Iconly-Document-1764597278746.svg' },
      { name: 'download', file: 'Iconly-Download-1764597309175.svg' },
      { name: 'download-white', file: 'Iconly-Download-1764597309175-white.svg' },
      { name: 'download-right', file: 'Iconly-Download-Right-1764597309175.svg' },
      { name: 'error', file: 'Iconly-Error-Triangle-1764597282369.svg' },
      { name: 'warning', file: 'Iconly-Error-Triangle-1764597282369.svg' },
      { name: 'flag', file: 'Iconly-Flag-1764597263799.svg' },
      { name: 'folder_open', file: 'Iconly-Folder-1764597253598.svg' },
      { name: 'table_chart', file: 'Iconly-Folder-File-Table-1764597312411.svg' },
      { name: 'hourglass_empty', file: 'Iconly-Hourglass-slim-1764597319390.svg' },
      { name: 'info', file: 'Iconly-info-circle-1764597315456.svg' },
      { name: 'lock', file: 'Iconly-Lock-1764597253608.svg' },
      { name: 'lock-white', file: 'Iconly-Lock-white.svg' },
      { name: 'more_horiz', file: 'Iconly-More-Circle-1764597253612.svg' },
      { name: 'play_circle', file: 'Iconly-Play-1764597253621.svg' },
      { name: 'play', file: 'Iconly-Play-1764597253621.svg' },
      { name: 'play_circle_white', file: 'Iconly-Play-white.svg' },
      { name: 'add_circle', file: 'Iconly-Plus-2-1764597264155.svg' },
      { name: 'search', file: 'Iconly-Search-1764597253626.svg' },
      { name: 'settings', file: 'Iconly-Setting-1764597253628.svg' },
      { name: 'visibility', file: 'Iconly-Show-1764597253730.svg' },
      { name: 'show', file: 'Iconly-Show-1764597253730.svg' },
      { name: 'show-white', file: 'Iconly-Show-white.svg' },
      { name: 'star', file: 'Iconly-Star-1764597253632.svg' },
      { name: 'star_border', file: 'Iconly-Star-1764597253632.svg' },
      { name: 'upload_file', file: 'Iconly-Upload-1764597309217.svg' },
      { name: 'upload_file_white', file: 'Iconly-Upload-white.svg' },
      { name: 'storage', file: 'Iconly-Folder-File-Table-1764597312411.svg' },
      { name: 'cloud_download', file: 'Iconly-Download-1764597309175.svg' },
      { name: 'radar', file: 'radar ai_.svg' },
      { name: 'analytics', file: 'Iconly-Chart-information-1764597309467.svg' },
      { name: 'bar_chart', file: 'Iconly-Chart-information-1764597309467.svg' },
      { name: 'bar_chart_white', file: 'Iconly-Chart-information-white.svg' },
      { name: 'expand', file: 'Expand.svg' },
      { name: 'apps', file: 'Iconly-Folder-File-Table-1764597312411.svg' },
      { name: 'upgrade', file: 'Iconly-Caret-Up-MD-1764597307000.svg' },
      { name: 'error_outline', file: 'Iconly-Error-Triangle-1764597282369.svg' },
      { name: 'content_copy', file: 'Iconly-Document-1764597278746.svg' },
      { name: 'cloud_upload', file: 'Iconly-Upload-1764597309217.svg' },
      { name: 'play_arrow', file: 'Iconly-Play-1764597253621.svg' },
      { name: 'visibility_off', file: 'Iconly-Close-remove-1764597270080.svg' },
      { name: 'pause_circle', file: 'Iconly-Clock-dashed-1764597319335.svg' },
      { name: 'help', file: 'Iconly-info-circle-1764597315456.svg' },
      { name: 'logout', file: 'Iconly-Logout-1765809067630.svg' },
      { name: 'lightbulb', file: 'lightbulb.svg' },
      { name: 'lightbulb-blue', file: 'lightbulb-blue.svg' },
      { name: 'metrics', file: 'metrics.svg' },
      { name: 'metrics-blue', file: 'metrics-blue.svg' },
      { name: 'summary-blue', file: 'summary-blue.svg' },
      { name: 'pencil', file: 'Pencil.svg' },
      // Blue action button variants
      { name: 'settings-blue', file: 'Iconly-Setting-1764597253628-blue.svg' },
      { name: 'visibility-blue', file: 'Iconly-Show-1764597253730-blue.svg' },
      { name: 'download-blue', file: 'Iconly-Download-1764597309175-blue.svg' },
      { name: 'pencil-blue', file: 'Pencil-blue.svg' },
      { name: 'folder_open-blue', file: 'Iconly-Folder-1764597253598-blue.svg' },
      { name: 'network-world', file: 'Network world_.svg' },
      { name: 'network-world-white', file: 'Network world_-white.svg' },
      // Orange action button variants
      { name: 'delete-orange', file: 'Iconly-Delete-1764597315594-orange.svg' }
    ];
    
    icons.forEach(icon => {
      this.iconRegistry.addSvgIcon(
        icon.name,
        this.sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}${icon.file}`)
      );
    });
  }
}
